import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ErrorDeApi, type Cliente, type PapelDeIva as Papel, type PapelDeIvaResumen } from '../servicios/api'
import { nombreDePeriodo, periodosDisponibles } from '../periodos'
import { PaqueteDeDeclaraciones } from '../componentes/PaqueteDeDeclaraciones'

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** `generadoEnUtc` es un instante: se convierte a hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

/** Los renglones que cierran el cálculo van resaltados: son los que se llevan a la declaración. */
const RENGLONES_DE_RESULTADO = new Set(['ResultadoDelPeriodo', 'ImpuestoAPagar', 'SaldoAFavorDelPeriodo'])

const mensaje = (e: unknown, respaldo: string) => (e instanceof ErrorDeApi ? e.message : respaldo)

/**
 * Papel de trabajo mensual de IVA (RF-1203). Arriba, el borrador: el cálculo de ahora mismo, que
 * cambia en cuanto se contabiliza o cancela un CFDI. Sellarlo lo congela con el origen de cada
 * importe y las tasas usadas (RN-32); abajo quedan los sellados del mes, el más reciente primero.
 */
export function PapelDeIva() {
  const { id: clienteId } = useParams<{ id: string }>()
  const hoy = new Date()
  const periodos = useMemo(() => periodosDisponibles(hoy), [hoy.getFullYear(), hoy.getMonth()])
  const [anio, setAnio] = useState(periodos[1].anio)
  const [mes, setMes] = useState(periodos[1].mes)
  const [cliente, setCliente] = useState<Cliente>()
  const [borrador, setBorrador] = useState<Papel>()
  const [errorBorrador, setErrorBorrador] = useState('')
  const [papeles, setPapeles] = useState<PapelDeIvaResumen[]>([])
  const [viendo, setViendo] = useState<Papel>()
  const [saldo, setSaldo] = useState('')
  const [origen, setOrigen] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [errorAccion, setErrorAccion] = useState('')
  const [errorDeLista, setErrorDeLista] = useState('')

  const saldoNumerico = Number(saldo) || 0

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch(() => setCliente(undefined))
  }, [clienteId])

  async function cargar(saldoAplicado = 0, origenDelSaldo = '') {
    if (!clienteId) return
    setErrorBorrador('')
    try {
      setBorrador(await api.borradorDePapelDeIva(clienteId, anio, mes, saldoAplicado, origenDelSaldo))
    } catch (e) {
      setBorrador(undefined)
      setErrorBorrador(mensaje(e, 'No se pudo calcular el papel de trabajo.'))
    }
    api.papelesDeIva(clienteId, anio, mes)
      .then((lista) => {
        setPapeles(lista)
        setErrorDeLista('')
      })
      .catch((e) => {
        setPapeles([])
        setErrorDeLista(mensaje(e, 'No se pudieron leer los papeles sellados de este mes.'))
      })
  }

  useEffect(() => {
    setViendo(undefined)
    setSaldo('')
    setOrigen('')
    setErrorAccion('')
    void cargar()
  }, [clienteId, anio, mes])

  async function sellar() {
    if (!clienteId) return
    setOcupado(true)
    setErrorAccion('')
    try {
      setViendo(await api.sellarPapelDeIva(clienteId, {
        anio, mes, saldoAFavorAplicado: saldoNumerico, ...(origen.trim() && { origenDelSaldoAFavor: origen.trim() }),
      }))
    } catch (e) {
      setErrorAccion(e instanceof ErrorDeApi && e.estado === 403
        ? 'Tu rol no puede sellar papeles de trabajo: pide a un contador o a un administrador del despacho que lo haga.'
        : mensaje(e, 'No se pudo sellar el papel de trabajo.'))
      return
    } finally {
      setOcupado(false)
    }
    // El papel ya quedó sellado y se muestra: si la lista no se recarga, eso es lo único que falló.
    try {
      setPapeles(await api.papelesDeIva(clienteId, anio, mes))
      setErrorDeLista('')
    } catch {
      setErrorDeLista('El papel se selló, pero no se pudo recargar la lista de sellados: recarga la página para verlo en ella.')
    }
  }

  async function ver(papelId: string) {
    if (!clienteId) return
    setErrorAccion('')
    try {
      setViendo(await api.papelDeIva(clienteId, papelId))
    } catch (e) {
      setErrorAccion(mensaje(e, 'No se pudo abrir el papel de trabajo.'))
    }
  }

  async function exportar(papel: { id: string; anio: number; mes: number }, formato: 'excel' | 'pdf') {
    if (!clienteId) return
    setErrorAccion('')
    try {
      await api.exportarPapelDeIva(clienteId, papel, formato)
    } catch (e) {
      setErrorAccion(mensaje(e, 'No se pudo exportar el papel de trabajo.'))
    }
  }

  if (!clienteId) return <p>Cargando…</p>

  const enPantalla = viendo ?? borrador

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente?.razonSocial ?? 'Expediente'}</Link></p>

      <header className="encabezado linea-base">
        <h1>Papel de trabajo de IVA</h1>
        <select
          className="compacta"
          style={{ width: 'auto' }}
          aria-label="Periodo"
          value={`${anio}-${mes}`}
          onChange={(e) => {
            const [a, m] = e.target.value.split('-')
            setAnio(Number(a))
            setMes(Number(m))
          }}
        >
          {periodos.map((p) => (
            <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>{nombreDePeriodo(p.anio, p.mes)}</option>
          ))}
        </select>
        {cliente && <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>}
      </header>

      <PaqueteDeDeclaraciones clienteId={clienteId} anio={anio} mes={mes} />

      <p className="nota">
        Base de flujo: el IVA entra al mes en que se cobró o se pagó (RN-29), y solo cuenta lo contabilizado.
        El borrador cambia en cuanto se contabiliza o cancela un CFDI; sellarlo lo congela tal como está.
      </p>

      {errorBorrador && <p className="error" role="alert">{errorBorrador}</p>}
      {errorAccion && <p className="error" role="alert">{errorAccion}</p>}

      {enPantalla && (
        <section aria-labelledby="titulo-papel">
          <h2 id="titulo-papel">
            {viendo?.generadoEnUtc
              ? `Sellado el ${instante(viendo.generadoEnUtc)}`
              : `Borrador · ${nombreDePeriodo(anio, mes)}`}
            {viendo && <> · <button className="enlace" onClick={() => setViendo(undefined)}>Volver al borrador</button></>}
          </h2>

          {enPantalla.advertencias.length > 0 && (
            <ul className="aviso" role="status" aria-label="Advertencias">
              {enPantalla.advertencias.map((a, i) => <li key={`${a.codigo}-${i}`}>{a.mensaje}</li>)}
            </ul>
          )}

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr><th scope="col">Concepto</th><th scope="col" className="num">Importe</th><th scope="col">Origen</th></tr>
              </thead>
              <tbody>
                {enPantalla.renglones.map((r) => (
                  <tr key={r.concepto}>
                    <th scope="row" style={RENGLONES_DE_RESULTADO.has(r.concepto) ? { fontWeight: 700 } : { fontWeight: 400 }}>
                      {r.etiqueta}
                    </th>
                    <td className="num">{dinero(r.importe)}</td>
                    <td className="sutil">{r.origen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {enPantalla.parametros.length > 0 && (
            <p className="nota">
              Tasas usadas: {enPantalla.parametros.map((p) =>
                `${p.parametro} ${p.valor} (${p.origen}${p.cotejado ? '' : '; sin cotejar'})`).join(' · ')}
            </p>
          )}

          {!viendo && (
            <form
              className="filtros"
              onSubmit={(e) => {
                e.preventDefault()
                void sellar()
              }}
            >
              <label>
                Saldo a favor de periodos anteriores aplicado
                <input type="number" min="0" step="0.01" value={saldo} onChange={(e) => setSaldo(e.target.value)} placeholder="0.00" />
              </label>
              <label>
                De qué declaración sale
                <input value={origen} onChange={(e) => setOrigen(e.target.value)} maxLength={300}
                  placeholder="Declaración de julio 2026, operación 123456" />
              </label>
              <button type="button" disabled={ocupado} onClick={() => void cargar(saldoNumerico, origen)}>Recalcular</button>
              <button type="submit" className="primario" disabled={ocupado}>
                {ocupado ? 'Sellando…' : 'Sellar papel de trabajo'}
              </button>
            </form>
          )}
        </section>
      )}

      <section aria-labelledby="titulo-sellados">
        <h2 id="titulo-sellados">Sellados de {nombreDePeriodo(anio, mes)}</h2>
        {errorDeLista && <p className="error" role="alert">{errorDeLista}</p>}
        {papeles.length === 0 ? (
          !errorDeLista && <p className="vacio">Aún no hay papeles sellados para este mes.</p>
        ) : (
          <div className="tarjeta"><ul className="lista-compacta">
            {papeles.map((p, i) => (
              <li key={p.id}>
                <span>
                  {instante(p.generadoEnUtc)}{i === 0 && ' · vigente'} · a pagar {dinero(p.impuestoAPagar)}
                  {p.saldoAFavorDelPeriodo > 0 && ` · saldo a favor ${dinero(p.saldoAFavorDelPeriodo)}`}
                  {p.tieneAdvertencias && ' · con advertencias'}
                </span>
                <span className="acciones-de-fila">
                  <button className="enlace" onClick={() => void ver(p.id)}>Ver</button>
                  <button className="enlace" onClick={() => void exportar(p, 'excel')}>.xlsx</button>
                  <button className="enlace" onClick={() => void exportar(p, 'pdf')}>PDF</button>
                </span>
              </li>
            ))}
          </ul></div>
        )}
      </section>
    </>
  )
}
